import type { ActiveColumnFilter } from '@app/stores/searchStore';

const MINIMUM_GLOBAL_SEARCH_LENGTH = 3;

/**
 * The full-text search term, or `undefined` while it is below the minimum
 * length the backend accepts.
 */
export function getFullTextSearchParam(
    searchText: string
): string | undefined {
    const trimmed = searchText?.trim() ?? '';

    return trimmed.length >= MINIMUM_GLOBAL_SEARCH_LENGTH
        ? trimmed
        : undefined;
}

/**
 * Merges the active column filters into a request-param object, one query param
 * per filter. The backend AND-s them with each other and with `search`.
 *
 * Correctness relies on the view's `filterConfig.filterKeys` only declaring
 * params the endpoint actually supports - an unsupported param makes the backend
 * reject the whole request with HTTP 400.
 */
export function withColumnFilters<T extends object>(
    params: T,
    filters: ActiveColumnFilter[]
): T {
    const applied = Object.fromEntries(
        filters
            .filter(filter => filter.value !== '')
            .map(filter => [filter.keyName, filter.value])
    );

    return { ...params, ...applied } as T;
}

/**
 * The legacy `filterProperty` / `filterValue` pair for endpoints that accept
 * a single column filter only. Takes the newest non-empty filter; both fields
 * are `undefined` when none is active.
 */
export function getSingleFilterParams(filters: ActiveColumnFilter[]): {
    filterProperty: string | undefined;
    filterValue: string | undefined;
} {
    const filter = filters.filter(f => f.value !== '').at(-1);

    return {
        filterProperty: filter?.keyName,
        filterValue: filter?.value,
    };
}
